<div class="rdp-client-container" *ngIf="approval">
  <nz-card
    nzTitle="Remote Desktop Access"
    [nzLoading]="connecting"
    [nzExtra]="connectionStatusTemplate"
    class="rdp-card">

    <!-- Connection Status Template -->
    <ng-template #connectionStatusTemplate>
      <nz-tag *ngIf="connection" [nzColor]="connectionStatusColor" class="connection-status">
        <i nz-icon [nzType]="getConnectionIcon()" nzTheme="outline"></i>
        {{ connectionStatusText }}
      </nz-tag>
    </ng-template>

    <!-- RDP Not Available -->
    <div *ngIf="!canConnectRdp" class="rdp-unavailable">
      <div class="unavailable-message">
        <p>{{ getUnavailableMessage() }}</p>
      </div>
    </div>

    <!-- RDP Available -->
    <div *ngIf="canConnectRdp" class="rdp-available">
      <!-- Connection Method Selection -->
      <div class="connection-methods">
        <nz-radio-group
          [(ngModel)]="selectedMethod"
          class="method-selector">
          <label nz-radio nzValue="web" class="method-option">
            <div class="method-content">
              <i nz-icon nzType="global" nzTheme="outline"></i>
              <div class="method-details">
                <div class="method-name">Web RDP</div>
                <div class="method-desc">Connect directly in browser</div>
              </div>
            </div>
          </label>

          <label nz-radio nzValue="native" class="method-option">
            <div class="method-content">
              <i nz-icon nzType="desktop" nzTheme="outline"></i>
              <div class="method-details">
                <div class="method-name">Windows RDP</div>
                <div class="method-desc">Use Remote Desktop app</div>
              </div>
            </div>
          </label>

          <label nz-radio nzValue="azure" class="method-option" *ngIf="isAzureVm">
            <div class="method-content">
              <i nz-icon nzType="cloud" nzTheme="outline"></i>
              <div class="method-details">
                <div class="method-name">Azure Portal</div>
                <div class="method-desc">Access via Azure Portal</div>
              </div>
            </div>
          </label>
        </nz-radio-group>
      </div>

      <!-- Connection Details -->
      <div class="connection-details">
        <nz-descriptions [nzColumn]="2" [nzSize]="'small'" nzBordered>
          <nz-descriptions-item nzTitle="Computer">
            <code>{{ approval.publicIpAddress }}</code>
            <button nz-button nzType="text" nzSize="small"
                    (click)="copyToClipboard(approval.publicIpAddress)"
                    nz-tooltip nzTooltipTitle="Copy IP address">
              <i nz-icon nzType="copy"></i>
            </button>
          </nz-descriptions-item>

          <nz-descriptions-item nzTitle="Username">
            <code>{{ approval.adminUsername }}</code>
            <button nz-button nzType="text" nzSize="small"
                    (click)="copyToClipboard(approval.adminUsername)"
                    nz-tooltip nzTooltipTitle="Copy username">
              <i nz-icon nzType="copy"></i>
            </button>
          </nz-descriptions-item>

          <nz-descriptions-item nzTitle="VM Name" [nzSpan]="2">
            <code>{{ approval.vmName }}</code>
          </nz-descriptions-item>

          <nz-descriptions-item nzTitle="Private IP" [nzSpan]="2" *ngIf="approval.privateIpAddress">
            <code>{{ approval.privateIpAddress }}</code>
          </nz-descriptions-item>
        </nz-descriptions>
      </div>

      <!-- Connection Controls -->
      <div class="connection-controls">
        <div class="primary-controls">
          <button nz-button
                  nzType="primary"
                  nzSize="large"
                  (click)="connectRdp()"
                  [disabled]="!canConnectRdp || connecting"
                  [nzLoading]="connecting">
            <i nz-icon [nzType]="getConnectionIcon()"></i>
            {{ getConnectButtonText() }}
          </button>

          <button nz-button
                  nzType="default"
                  nzSize="large"
                  (click)="disconnectRdp()"
                  [disabled]="!connection || connection.status === 'disconnected'">
            <i nz-icon nzType="disconnect"></i>
            Disconnect
          </button>
        </div>

        <div class="secondary-controls">
          <button nz-button
                  nzType="default"
                  (click)="showAdvancedOptions = !showAdvancedOptions">
            <i nz-icon [nzType]="showAdvancedOptions ? 'up' : 'down'"></i>
            Advanced Options
          </button>
        </div>
      </div>

      <!-- Advanced Options -->
      <div class="advanced-options" *ngIf="showAdvancedOptions">
        <nz-collapse>
          <nz-collapse-panel
            nzHeader="Connection Information"
            [nzActive]="false">

            <div class="connection-info">
              <h4>Session Details</h4>
              <ul class="info-list">
                <li *ngIf="connection">
                  <strong>Session ID:</strong> {{ connection.id || 'Unknown' }}
                </li>
                <li *ngIf="connection?.startTime">
                  <strong>Started:</strong> {{ connection?.startTime | date:'medium' }}
                </li>
                <li *ngIf="connection?.startTime">
                  <strong>Duration:</strong> {{ formatDuration(connection?.startTime) }}
                </li>
                <li *ngIf="connection?.sessionUrl">
                  <strong>Session URL:</strong>
                  <code>{{ connection?.sessionUrl }}</code>
                </li>
              </ul>
            </div>

            <div class="connection-commands">
              <h4>Manual Connection Commands</h4>
              <nz-segmented [nzOptions]="commandOptions" (nzChange)="onCommandSelect($event)"></nz-segmented>

              <div class="command-output" *ngIf="selectedCommand">
                <code>{{ selectedCommand }}</code>
                <button nz-button nzType="text" nzSize="small"
                        (click)="copyToClipboard(selectedCommand)">
                  <i nz-icon nzType="copy"></i>
                </button>
              </div>
            </div>
          </nz-collapse-panel>

          <nz-collapse-panel
            nzHeader="Security & Help"
            [nzActive]="false">

            <div class="security-info">
              <nz-alert
                nzType="info"
                nzTitle="Security Notice"
                nzDescription="All RDP sessions are logged and monitored. Sessions are automatically terminated after 4 hours for security reasons."
                nzShowIcon
                [nzCloseable]="false">
              </nz-alert>

              <div class="help-links">
                <h4>Help & Support</h4>
                <ul>
                  <li><a href="#" (click)="showHelp('troubleshooting')">Troubleshooting</a></li>
                  <li><a href="#" (click)="showHelp('security')">Security Information</a></li>
                  <li><a href="#" (click)="showHelp('contact')">Contact Support</a></li>
                </ul>
              </div>
            </div>
          </nz-collapse-panel>
        </nz-collapse>
      </div>
    </div>
  </nz-card>
</div>